import {
  Button,
  NotificationCard,
  RHFTextField,
  Section,
} from "@arp/react-ndbx";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Fade, Modal, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import React, { useEffect, useState } from "react";
import { optVerificationModalContent } from "../../../config/const/enums";
import IdentityHelper from "../../../utilities/IdentityHelper";
import { getModalDefaultStyle } from "../../../utilities/getFontValues";
import { formatMobileNumberfromOktaMasked } from "../../../utilities/mappingHelper";
import {
  getOktaSmsTriggerErrorCode,
  oktaProceedAuthenticator,
  oktaTriggerSMS,
} from "../../../utilities/oktaVerificationUtil";
import RetrieveOtpVerificationCodeModal from "./RetrieveOtpVerificationCodeModal";
import { onlineRetrieveDisclaimer } from "../../../config/disclaimers/disclaimers";

function RetrieveOtpVerificationModal({
  openOtpVerification,
  handleOtpVerificationClose,
  verifyOtpNumberModal,
  otpSendSuccess,
  quoteRefNumber,
  mobileNumber,
}) {
  const theme = useTheme();
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [oktaErrorFlag, setOktaErrorFlag] = useState(false);
  const [oktaErrorMessage, setOktaErrorMessage] = useState("");

  const style = getModalDefaultStyle(theme);

  useEffect(() => {
    setOktaErrorFlag(false);
    if(openOtpVerification && "true" === import.meta.env.VITE_SKIP_SEND_SMS_MODAL) {
      verifyOtpNumberModal();
      sendOtpCode();
    }
  }, [openOtpVerification]);

  const sendOtpCode = async () => {
    try {
      if (quoteRefNumber) {
        setSendOtpLoading(true);
        let oktaClient = IdentityHelper.getOktaAuthClient();
        await oktaProceedAuthenticator(oktaClient);
        const smsSendResponse = await oktaTriggerSMS(oktaClient);
        if (smsSendResponse.status === "TERMINAL") {
          setOktaErrorFlag(true);
          const oktaErrorCode = getOktaSmsTriggerErrorCode(
            smsSendResponse?.messages
          );
          if (oktaErrorCode === "errors.E0000150") {
            setOktaErrorMessage(optVerificationModalContent.SMS_SEND_WAIT);
          } else {
            setResendSMSFailedMessage(
              optVerificationModalContent.GENERIC_SMS_ERROR
            );
          }
          setSendOtpLoading(false);
        } else {
          setTimeout(() => {
            setSendOtpLoading(false);
            verifyOtpNumberModal();
          }, 1000);
        }
      }
    } catch (error) {
      console.error("error occured while sending SMS - ", error);
      setOktaErrorFlag(true);
      setOktaErrorMessage(
        "Unable to send sms request, please try again later."
      );
      setSendOtpLoading(false);
    }
  };

  return (
    <Modal open={openOtpVerification} onClose={handleOtpVerificationClose}>
      <Box sx={style} data-identifier="save-send-email-modal">
        <Section
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            mt: 0,
            mb: 0,
            mr: { xs: 1, sm: 3 },
            position: "sticky",
            top: { xs: 16, sm: 24 },
          }}
        >
          <CloseIcon
            onClick={handleOtpVerificationClose}
            justify="center"
            alignItems="center"
            sx={{ cursor: "pointer" }}
          ></CloseIcon>
        </Section>
        {!otpSendSuccess && (
          <Section
            sx={{
              pl: { xs: 4, sm: 8 },
              pr: { xs: 4, sm: 8 },
              pt: { xs: 5 },
              mt: 0,
              mb: 0,
            }}
          >
            <Section sx={{ mt: 0, mb: 3 }}>
              <Typography variant="h3-bold">
                {onlineRetrieveDisclaimer.RETRIEVE_MODAL_TITLE}
              </Typography>
            </Section>
            <Section sx={{ maxWidth: { xs: 258 }, mb: 4 }}>
              <Section md={12} mt={{ sm: 2, md: 4 }}>
                <RHFTextField
                  className="subvariant-text-input"
                  variant="standard"
                  label="Mobile number"
                  name="mobileNumber"
                  defaultValue={formatMobileNumberfromOktaMasked(mobileNumber)}
                  type="phone"
                  disabled
                />
              </Section>
            </Section>

            <Section sx={{ mb: 3, mt: 0 }}>
              <Typography
                id="modal-modal-description"
                variant="p3"
                sx={{ mt: 2 }}
              >
                {onlineRetrieveDisclaimer.RETRIEVE_MODAL_DESCRIPTION}
              </Typography>
            </Section>
            <Section mb={4}>
              <Fade in={true} timeout={500}>
                <Box>
                  <NotificationCard
                    cardType="info"
                    title={
                      onlineRetrieveDisclaimer.RETRIEVE_MODAL_NOTIFICATION_TITLE
                    }
                    message={
                      onlineRetrieveDisclaimer.RETRIEVE_MODAL_NOTIFICATION
                    }
                  />
                </Box>
              </Fade>
            </Section>
            {oktaErrorFlag && (
              <Section mb={4}>
                <Fade in={true} timeout={500}>
                  <Box>
                    <NotificationCard
                      cardType="error"
                      title=""
                      message={oktaErrorMessage}
                    />
                  </Box>
                </Fade>
              </Section>
            )}
            <Section mb={3}>
              <Section sx={{ maxWidth: { xs: "250px" } }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={sendOtpCode}
                  disabled={sendOtpLoading}
                >
                  {onlineRetrieveDisclaimer.RETRIEVE_MODAL_SEND_CODE}
                </Button>
              </Section>
            </Section>
          </Section>
        )}
        {otpSendSuccess && (
          <RetrieveOtpVerificationCodeModal
            userMobileNumber={mobileNumber}
            quoteRefNumber={quoteRefNumber}
          />
        )}
      </Box>
    </Modal>
  );
}

export default RetrieveOtpVerificationModal;
